// TanStack Query hooks for the Sites module. Sites are physical posting
// locations belonging to an organization; each owns nested posts, and each post
// owns versioned post_orders. Mirrors packages/api/src/routes/sites.ts.
//
// Post orders are versioned per post: drafts are created/edited freely, while
// the dedicated publish endpoint (gated on post_order.publish) archives the
// currently-active order and inserts a fresh active version.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePostInput,
  CreatePostOrderInput,
  CreateSiteInput,
  Post,
  PostOrder,
  PostOrderResponse,
  PostOrdersListResponse,
  PostResponse,
  PostsListResponse,
  Site,
  SiteResponse,
  SiteStatus,
  SiteType,
  SitesListResponse,
  UpdateSiteInput,
} from '@dsr/shared-types';
import { api } from '../client';

// =============================================================================
// Sites
// =============================================================================

export interface SiteFilters {
  organization_id?: number;
  status?: SiteStatus;
  site_type?: SiteType;
  q?: string;
}

export function useSites(filters: SiteFilters = {}) {
  const params = new URLSearchParams();
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.site_type) params.set('site_type', filters.site_type);
  if (filters.q && filters.q.trim().length > 0) params.set('q', filters.q.trim());
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/sites?${qs}` : '/api/sites';
  return useQuery({
    queryKey: ['sites', { ...filters }],
    queryFn: ({ signal }) => api.get<SitesListResponse>(url, signal),
  });
}

export function useSite(id: number | null) {
  return useQuery({
    queryKey: ['site', id],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) => api.get<SiteResponse>(`/api/sites/${id}`, signal),
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSiteInput) =>
      api.post<SiteResponse>('/api/sites', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export interface UpdateSiteVars extends UpdateSiteInput {
  id: number;
}

export function useUpdateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateSiteVars) =>
      api.patch<SiteResponse>(`/api/sites/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: ['site', vars.id] });
    },
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/sites/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

// =============================================================================
// Posts (nested under a site)
// =============================================================================

export function usePosts(siteId: number | null) {
  return useQuery({
    queryKey: ['posts', siteId],
    enabled: siteId !== null && siteId > 0,
    queryFn: ({ signal }) =>
      api.get<PostsListResponse>(`/api/sites/${siteId}/posts`, signal),
  });
}

export interface CreatePostVars extends CreatePostInput {
  siteId: number;
}

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, ...input }: CreatePostVars) =>
      api.post<PostResponse>(`/api/sites/${siteId}/posts`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['posts', vars.siteId] });
    },
  });
}

// =============================================================================
// Post orders (nested under a post — :postId on the wire)
//
// The list/create endpoints key off the POST id, not the site id.
// =============================================================================

export function usePostOrders(postId: number | null) {
  return useQuery({
    queryKey: ['post-orders', postId],
    enabled: postId !== null && postId > 0,
    queryFn: ({ signal }) =>
      api.get<PostOrdersListResponse>(`/api/sites/${postId}/post-orders`, signal),
  });
}

export interface CreatePostOrderVars extends CreatePostOrderInput {
  postId: number;
}

// Creates the next DRAFT version for the post. Does not activate.
export function useCreatePostOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, ...input }: CreatePostOrderVars) =>
      api.post<PostOrderResponse>(`/api/sites/${postId}/post-orders`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['post-orders', vars.postId] });
    },
  });
}

// Publishes a fresh ACTIVE post order: archives the currently-active order and
// inserts a new version with status='active'. Gated server-side on
// post_order.publish (separate from post_order.write).
export function usePublishPostOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, ...input }: CreatePostOrderVars) =>
      api.post<PostOrderResponse>(`/api/sites/${postId}/post-orders/publish`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['post-orders', vars.postId] });
    },
  });
}

// Re-export the wire types callers most often need alongside the hooks.
export type { Site, Post, PostOrder };
